import { useEffect, useCallback } from "react";
import api from "../lib/api";

/**
 * IDGAppearanceLoader — fetches /api/idg/appearance once on mount and applies:
 *  - CSS variables for theme colors
 *  - favicon link in <head>
 *  - injected <style id="idg-custom-css">
 *  - injected <script id="idg-custom-js">
 *
 * Re-applies instantly when:
 *  - window event "idg:appearance-updated" fires (ACP saves colors/fonts/etc.)
 *  - window event "idg:theme-css-updated" fires (ACP saves index.css source file)
 *
 * This component renders nothing. Mount once near the top of the React tree.
 */
export default function IDGAppearanceLoader() {
  const applyAppearance = useCallback((a) => {
    if (!a) return;
    const root = document.documentElement;

    // CSS Variables — applied to :root instantly
    if (a.color_primary)   root.style.setProperty("--tf-primary", a.color_primary);
    if (a.color_secondary) root.style.setProperty("--tf-primary-hover", a.color_secondary);
    if (a.color_accent)    root.style.setProperty("--idg-accent", a.color_accent);
    if (a.color_bg)        root.style.setProperty("--idg-bg", a.color_bg);
    if (a.color_text)      root.style.setProperty("--idg-text", a.color_text);
    if (a.color_link)      root.style.setProperty("--idg-link", a.color_link);
    if (a.base_font_size)  root.style.setProperty("--idg-font-size", `${a.base_font_size}px`);

    // Background Image — inject a <style> tag to override with max priority
    let bgStyle = document.getElementById("idg-bg-override");
    if (a.background_url) {
      if (!bgStyle) {
        bgStyle = document.createElement("style");
        bgStyle.id = "idg-bg-override";
        document.head.appendChild(bgStyle);
      }
      bgStyle.innerHTML = `
        html body {
          background: #050505 !important;
        }
        html body #root {
          background: transparent !important;
        }
        html body::before {
          content: '';
          position: fixed;
          inset: 0;
          z-index: 0;
          background-image: url("${a.background_url}");
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
          opacity: 0.7;
          pointer-events: none;
          will-change: transform;
          transform: translateZ(0);
        }
        html body > * {
          position: relative;
          z-index: 1;
        }
      `;
    } else {
      if (bgStyle) bgStyle.remove();
    }

    // Favicon
    if (a.favicon_url) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = a.favicon_url + "?t=" + Date.now();
    }

    // Custom CSS Injection
    let style = document.getElementById("idg-custom-css");
    if (a.custom_css) {
      if (!style) {
        style = document.createElement("style");
        style.id = "idg-custom-css";
        document.head.appendChild(style);
      }
      style.innerHTML = a.custom_css;
    } else if (style) {
      style.remove();
    }

    // Custom JS Injection
    let script = document.getElementById("idg-custom-js");
    if (a.custom_js) {
      if (!script) {
        script = document.createElement("script");
        script.id = "idg-custom-js";
        document.body.appendChild(script);
      }
      script.innerHTML = a.custom_js;
    } else if (script) {
      script.remove();
    }
  }, []);

  const fetchAndApplyAppearance = useCallback(() => {
    api.get("/idg/appearance")
      .then((r) => applyAppearance(r.data || {}))
      .catch(() => {});
  }, [applyAppearance]);

  const fetchAndApplyThemeCSS = useCallback(() => {
    // Fetch the live index.css source from the backend and inject it as an
    // overriding <style> tag at the end of <head>, so edits appear instantly.
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${backendUrl}/app-api/idg/theme/css`, { cache: "no-store" })
      .then((res) => res.text())
      .then((css) => {
        let liveStyle = document.getElementById("idg-theme-css-live");
        if (!liveStyle) {
          liveStyle = document.createElement("style");
          liveStyle.id = "idg-theme-css-live";
          document.head.appendChild(liveStyle);
        }
        liveStyle.innerHTML = css;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Initial load of appearance settings
    fetchAndApplyAppearance();

    // Listen for ACP saves — appearance settings (colors, fonts, logo, etc.)
    window.addEventListener("idg:appearance-updated", fetchAndApplyAppearance);
    // Listen for ACP saves — index.css source file changes
    window.addEventListener("idg:theme-css-updated", fetchAndApplyThemeCSS);

    return () => {
      window.removeEventListener("idg:appearance-updated", fetchAndApplyAppearance);
      window.removeEventListener("idg:theme-css-updated", fetchAndApplyThemeCSS);
    };
  }, [fetchAndApplyAppearance, fetchAndApplyThemeCSS]);

  return null;
}
